import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Building2, FileText, Upload, Receipt, Search, Plus, Settings, 
  ChevronRight, Loader2, Check, X, AlertTriangle, Eye, Download,
  Calendar, Filter, RefreshCw, Trash2, Edit, Camera, FileSpreadsheet,
  Send, DollarSign, Clock, CheckCircle, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// Insurance Companies Management Component
const InsuranceCompanies = ({ token, onSelectCompany }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    default_rate: 80,
    voucher_validity_days: 30
  });

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/insurance/companies/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Erreur lors du chargement des assurances');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleSave = async () => {
    try {
      if (editingCompany) {
        await axios.put(`${API_URL}/insurance/companies/${editingCompany.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Assurance mise à jour');
      } else {
        await axios.post(`${API_URL}/insurance/companies/`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Assurance créée');
      }
      setShowAddDialog(false);
      setEditingCompany(null);
      setFormData({ name: '', code: '', default_rate: 80, voucher_validity_days: 30 });
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const openEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      code: company.code,
      default_rate: company.default_rate,
      voucher_validity_days: company.voucher_validity_days || 30
    });
    setShowAddDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Assurances partenaires ({companies.length})</h3>
        <Button onClick={() => { setEditingCompany(null); setFormData({ name: '', code: '', default_rate: 80, voucher_validity_days: 30 }); setShowAddDialog(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Ajouter
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {companies.map(company => (
          <Card key={company.id} className="cursor-pointer hover:border-cyan-500 transition-colors" onClick={() => onSelectCompany(company)}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-semibold">{company.name}</h4>
                  <p className="text-sm text-gray-500">{company.code}</p>
                </div>
                <Badge variant="outline">{company.default_rate}%</Badge>
              </div>
              <div className="mt-3 flex items-center gap-4 text-sm text-gray-500">
                <span>{company.exclusions_count || 0} exclusions</span>
                <span>{company.vouchers_count || 0} bons</span>
              </div>
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openEdit(company); }}>
                  <Edit className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onSelectCompany(company); }}>
                  <ChevronRight className="w-3 h-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCompany ? 'Modifier l\'assurance' : 'Nouvelle assurance'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom</Label>
              <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="NSIA Assurances" />
            </div>
            <div>
              <Label>Code</Label>
              <Input value={formData.code} onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})} placeholder="NSIA" />
            </div>
            <div>
              <Label>Taux de remboursement par défaut (%)</Label>
              <Input type="number" value={formData.default_rate} onChange={e => setFormData({...formData, default_rate: parseFloat(e.target.value)})} />
            </div>
            <div>
              <Label>Validité du bon (jours)</Label>
              <Input type="number" value={formData.voucher_validity_days} onChange={e => setFormData({...formData, voucher_validity_days: parseInt(e.target.value)})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
            <Button onClick={handleSave}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Exclusions Management Component
const ExclusionsManager = ({ token, company, onBack }) => {
  const [exclusions, setExclusions] = useState({ by_category: [], by_pattern: [], by_product: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importType, setImportType] = useState('excel');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [extractedExclusions, setExtractedExclusions] = useState([]);
  const [showAddManual, setShowAddManual] = useState(false);
  const [manualExclusion, setManualExclusion] = useState({ type: 'product', value: '', reason: '' });

  const fetchExclusions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/insurance/exclusions/${company.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExclusions(response.data);
    } catch (error) {
      console.error('Error fetching exclusions:', error);
    } finally {
      setLoading(false);
    }
  }, [token, company.id]);

  useEffect(() => {
    fetchExclusions();
  }, [fetchExclusions]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('insurance_id', company.id);

    try {
      const endpoint = importType === 'excel' ? 'import/excel' : 'import/ocr';
      const response = await axios.post(`${API_URL}/insurance/exclusions/${endpoint}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (importType === 'ocr' && response.data.requires_validation) {
        setExtractedExclusions(response.data.exclusions);
        toast.info(`${response.data.exclusions.length} exclusions détectées - Validez avant import`);
      } else {
        toast.success(response.data.message);
        fetchExclusions();
        setShowImportDialog(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setUploadingFile(false);
    }
  };

  const confirmOCRImport = async () => {
    try {
      await axios.post(`${API_URL}/insurance/exclusions/bulk`, {
        insurance_id: company.id,
        exclusions: extractedExclusions.filter(e => e.selected !== false)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Exclusions importées');
      setExtractedExclusions([]);
      setShowImportDialog(false);
      fetchExclusions();
    } catch (error) {
      toast.error('Erreur lors de l\'import');
    }
  };

  const initDefaultCategories = async () => {
    try {
      const response = await axios.post(`${API_URL}/insurance/exclusions/initialize-categories/${company.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchExclusions();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const addManualExclusion = async () => {
    try {
      await axios.post(`${API_URL}/insurance/exclusions/`, {
        insurance_id: company.id,
        ...manualExclusion
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Exclusion ajoutée');
      setShowAddManual(false);
      setManualExclusion({ type: 'product', value: '', reason: '' });
      fetchExclusions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const deleteExclusion = async (id) => {
    try {
      await axios.delete(`${API_URL}/insurance/exclusions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Exclusion supprimée');
      fetchExclusions();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack}>
            ← Retour
          </Button>
          <h3 className="text-lg font-semibold">Exclusions - {company.name}</h3>
          <Badge>{exclusions.total} exclusions</Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={initDefaultCategories}>
            Catégories par défaut
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowAddManual(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Ajouter
          </Button>
          <Button size="sm" onClick={() => setShowImportDialog(true)}>
            <Upload className="w-4 h-4 mr-1" />
            Importer
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Categories */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Catégories exclues</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {exclusions.by_category.length === 0 ? (
                <p className="text-sm text-gray-500">Aucune catégorie</p>
              ) : exclusions.by_category.map(e => (
                <div key={e.id} className="flex items-center justify-between p-2 bg-red-50 rounded text-sm">
                  <span>{e.value}</span>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => deleteExclusion(e.id)}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Patterns */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Patterns</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {exclusions.by_pattern.length === 0 ? (
                <p className="text-sm text-gray-500">Aucun pattern</p>
              ) : exclusions.by_pattern.map(e => (
                <div key={e.id} className="flex items-center justify-between p-2 bg-orange-50 rounded text-sm">
                  <span>{e.value}</span>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => deleteExclusion(e.id)}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Products */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Produits exclus ({exclusions.by_product.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-64 overflow-y-auto">
              {exclusions.by_product.length === 0 ? (
                <p className="text-sm text-gray-500">Aucun produit</p>
              ) : exclusions.by_product.slice(0, 20).map(e => (
                <div key={e.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                  <span className="truncate flex-1">{e.value}</span>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => deleteExclusion(e.id)}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              {exclusions.by_product.length > 20 && (
                <p className="text-xs text-gray-400">+ {exclusions.by_product.length - 20} autres</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Importer des exclusions</DialogTitle>
            <DialogDescription>Choisissez le format du fichier à importer</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-4">
              <Button variant={importType === 'excel' ? 'default' : 'outline'} onClick={() => setImportType('excel')} className="flex-1">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel / CSV
              </Button>
              <Button variant={importType === 'ocr' ? 'default' : 'outline'} onClick={() => setImportType('ocr')} className="flex-1">
                <Camera className="w-4 h-4 mr-2" />
                Photo / Image
              </Button>
            </div>

            <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
              <input
                type="file"
                accept={importType === 'excel' ? '.xlsx,.xls,.csv' : '.jpg,.jpeg,.png,.webp'}
                onChange={handleFileUpload}
                className="hidden"
                id="file-upload"
                disabled={uploadingFile}
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                {uploadingFile ? (
                  <Loader2 className="w-12 h-12 mx-auto text-cyan-500 animate-spin" />
                ) : (
                  <Upload className="w-12 h-12 mx-auto text-gray-400" />
                )}
                <p className="mt-2 text-sm text-gray-600">
                  {importType === 'excel' ? 'Cliquez pour sélectionner un fichier Excel ou CSV' : 'Cliquez pour sélectionner une image'}
                </p>
              </label>
            </div>

            {/* OCR Results */}
            {extractedExclusions.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium">Exclusions détectées ({extractedExclusions.length})</h4>
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {extractedExclusions.map((e, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                      <input 
                        type="checkbox" 
                        checked={e.selected !== false}
                        onChange={(ev) => {
                          const updated = [...extractedExclusions];
                          updated[i].selected = ev.target.checked;
                          setExtractedExclusions(updated);
                        }}
                      />
                      <Badge variant="outline" className="text-xs">{e.type}</Badge>
                      <span className="flex-1 text-sm">{e.value}</span>
                      <span className="text-xs text-gray-500">{e.reason}</span>
                    </div>
                  ))}
                </div>
                <Button onClick={confirmOCRImport} className="w-full">
                  <Check className="w-4 h-4 mr-2" />
                  Importer {extractedExclusions.filter(e => e.selected !== false).length} exclusions
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Manual Dialog */}
      <Dialog open={showAddManual} onOpenChange={setShowAddManual}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une exclusion</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Type</Label>
              <Select value={manualExclusion.type} onValueChange={v => setManualExclusion({...manualExclusion, type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="category">Catégorie</SelectItem>
                  <SelectItem value="pattern">Pattern</SelectItem>
                  <SelectItem value="product">Produit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Valeur</Label>
              <Input 
                value={manualExclusion.value} 
                onChange={e => setManualExclusion({...manualExclusion, value: e.target.value})}
                placeholder={manualExclusion.type === 'pattern' ? 'VITAMINE*' : 'COSMETIQUE'}
              />
            </div>
            <div>
              <Label>Motif</Label>
              <Input 
                value={manualExclusion.reason} 
                onChange={e => setManualExclusion({...manualExclusion, reason: e.target.value})}
                placeholder="Non remboursable"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddManual(false)}>Annuler</Button>
            <Button onClick={addManualExclusion}>Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Voucher Processing Component
const VoucherProcessor = ({ token, companies }) => {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [step, setStep] = useState(1); // 1: select company, 2: scan/enter data, 3: verify, 4: done
  const [scanning, setScanning] = useState(false);
  const [voucherData, setVoucherData] = useState({
    voucher_number: '',
    beneficiary_name: '',
    insured_name: '',
    beneficiary_age: '',
    reimbursement_rate: 80,
    voucher_date: new Date().toISOString().split('T')[0],
    products: []
  });
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [totals, setTotals] = useState({ gross: 0, insurance: 0, patient: 0, excluded: 0 });

  useEffect(() => {
    // Recalculate totals when products or rate changes
    let gross = 0, insurance = 0, patient = 0, excluded = 0;
    
    voucherData.products.forEach(p => {
      const total = p.unit_price * p.quantity;
      gross += total;
      if (p.is_excluded) {
        excluded += total;
        patient += total;
      } else {
        const ins = Math.round(total * (voucherData.reimbursement_rate / 100));
        insurance += ins;
        patient += (total - ins);
      }
    });
    
    setTotals({ gross, insurance, patient, excluded });
  }, [voucherData.products, voucherData.reimbursement_rate]);

  const searchProducts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setProductResults([]);
      return;
    }
    setSearchingProducts(true);
    try {
      const response = await axios.get(`${API_URL}/products/search?q=${encodeURIComponent(query)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductResults(response.data);
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setSearchingProducts(false);
    }
  }, [token]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearch) searchProducts(productSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch, searchProducts]);

  const addProduct = async (product) => {
    // Check if excluded
    let isExcluded = false;
    let exclusionReason = null;
    
    if (selectedCompany) {
      try {
        const response = await axios.get(
          `${API_URL}/insurance/exclusions/check/${selectedCompany.id}/${product.code || product.name}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        isExcluded = response.data.excluded;
        exclusionReason = response.data.reason;
      } catch (error) {
        console.error('Error checking exclusion:', error);
      }
    }
    
    const newProduct = {
      code: product.code,
      name: product.name,
      quantity: 1,
      unit_price: product.price || 0,
      is_excluded: isExcluded,
      exclusion_reason: exclusionReason
    };
    
    setVoucherData(prev => ({
      ...prev,
      products: [...prev.products, newProduct]
    }));
    
    setProductSearch('');
    setProductResults([]);
    
    if (isExcluded) {
      toast.warning(`${product.name} est exclu: ${exclusionReason}`);
    }
  };

  const removeProduct = (index) => {
    setVoucherData(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
  };

  const updateProductQty = (index, qty) => {
    setVoucherData(prev => ({
      ...prev,
      products: prev.products.map((p, i) => i === index ? { ...p, quantity: Math.max(1, qty) } : p)
    }));
  };

  const handleScanImage = async (file) => {
    if (!selectedCompany) {
      toast.error('Sélectionnez d\'abord une assurance');
      return;
    }
    
    setScanning(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('insurance_id', selectedCompany.id);
    
    try {
      const response = await axios.post(`${API_URL}/insurance/vouchers/scan`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const data = response.data.extracted_data;
      setVoucherData(prev => ({
        ...prev,
        voucher_number: data.voucher_number || prev.voucher_number,
        beneficiary_name: data.beneficiary_name || prev.beneficiary_name,
        insured_name: data.insured_name || prev.insured_name,
        beneficiary_age: data.beneficiary_age || prev.beneficiary_age,
        voucher_date: data.voucher_date || prev.voucher_date,
        products: data.products?.map(p => ({
          code: p.code,
          name: p.name,
          quantity: p.quantity || 1,
          unit_price: p.unit_price || 0,
          is_excluded: false,
          exclusion_reason: null
        })) || prev.products
      }));
      
      toast.success(`Scan réussi - Confiance: ${Math.round(response.data.confidence * 100)}%`);
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du scan');
    } finally {
      setScanning(false);
    }
  };

  const submitVoucher = async () => {
    if (!selectedCompany) {
      toast.error('Sélectionnez une assurance');
      return;
    }
    if (!voucherData.beneficiary_name) {
      toast.error('Entrez le nom du bénéficiaire');
      return;
    }
    if (voucherData.products.length === 0) {
      toast.error('Ajoutez au moins un produit');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/insurance/vouchers/`, {
        insurance_id: selectedCompany.id,
        ...voucherData,
        beneficiary_age: voucherData.beneficiary_age ? parseInt(voucherData.beneficiary_age) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Bon ${response.data.voucher_number} enregistré`);
      setStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const resetForm = () => {
    setSelectedCompany(null);
    setStep(1);
    setVoucherData({
      voucher_number: '',
      beneficiary_name: '',
      insured_name: '',
      beneficiary_age: '',
      reimbursement_rate: 80,
      voucher_date: new Date().toISOString().split('T')[0],
      products: []
    });
  };

  return (
    <div className="space-y-4">
      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {[
          { num: 1, label: 'Assurance' },
          { num: 2, label: 'Saisie' },
          { num: 3, label: 'Vérification' },
          { num: 4, label: 'Terminé' }
        ].map((s, i) => (
          <React.Fragment key={s.num}>
            <div className={`flex items-center gap-2 ${step >= s.num ? 'text-cyan-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= s.num ? 'bg-cyan-600 text-white' : 'bg-gray-200'}`}>
                {step > s.num ? <Check className="w-4 h-4" /> : s.num}
              </div>
              <span className="text-sm hidden sm:inline">{s.label}</span>
            </div>
            {i < 3 && <div className={`w-8 h-0.5 ${step > s.num ? 'bg-cyan-600' : 'bg-gray-200'}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Select Company */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Sélectionnez l'assurance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {companies.map(c => (
                <Button
                  key={c.id}
                  variant={selectedCompany?.id === c.id ? 'default' : 'outline'}
                  className="h-auto py-3 flex flex-col items-center"
                  onClick={() => {
                    setSelectedCompany(c);
                    setVoucherData(prev => ({ ...prev, reimbursement_rate: c.default_rate }));
                  }}
                >
                  <span className="font-medium">{c.code}</span>
                  <span className="text-xs opacity-70">{c.default_rate}%</span>
                </Button>
              ))}
            </div>
            {selectedCompany && (
              <div className="mt-4 flex justify-end">
                <Button onClick={() => setStep(2)}>
                  Continuer <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Enter Data or Scan */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Saisie du bon - {selectedCompany?.name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Scan option */}
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => e.target.files[0] && handleScanImage(e.target.files[0])}
                className="hidden"
                id="scan-voucher"
                disabled={scanning}
              />
              <label htmlFor="scan-voucher" className="cursor-pointer">
                {scanning ? (
                  <Loader2 className="w-8 h-8 mx-auto text-cyan-500 animate-spin" />
                ) : (
                  <Camera className="w-8 h-8 mx-auto text-gray-400" />
                )}
                <p className="mt-2 text-sm text-gray-600">Scanner le bon (OCR)</p>
              </label>
            </div>

            <div className="text-center text-sm text-gray-500">— ou saisie manuelle —</div>

            {/* Manual entry */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>N° Bon</Label>
                <Input 
                  value={voucherData.voucher_number} 
                  onChange={e => setVoucherData({...voucherData, voucher_number: e.target.value})}
                  placeholder="BON-2026-001"
                />
              </div>
              <div>
                <Label>Date du bon</Label>
                <Input 
                  type="date"
                  value={voucherData.voucher_date} 
                  onChange={e => setVoucherData({...voucherData, voucher_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Nom du bénéficiaire *</Label>
                <Input 
                  value={voucherData.beneficiary_name} 
                  onChange={e => setVoucherData({...voucherData, beneficiary_name: e.target.value})}
                  placeholder="KOUASSI Marie"
                />
              </div>
              <div>
                <Label>Nom de l'assuré</Label>
                <Input 
                  value={voucherData.insured_name} 
                  onChange={e => setVoucherData({...voucherData, insured_name: e.target.value})}
                  placeholder="KOUASSI Jean"
                />
              </div>
              <div>
                <Label>Âge</Label>
                <Input 
                  type="number"
                  value={voucherData.beneficiary_age} 
                  onChange={e => setVoucherData({...voucherData, beneficiary_age: e.target.value})}
                />
              </div>
              <div>
                <Label>Taux (%)</Label>
                <Input 
                  type="number"
                  value={voucherData.reimbursement_rate} 
                  onChange={e => setVoucherData({...voucherData, reimbursement_rate: parseFloat(e.target.value)})}
                />
              </div>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>Retour</Button>
              <Button onClick={() => setStep(3)}>Continuer</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Add Products & Verify */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>Vérification - {selectedCompany?.name}</CardTitle>
            <CardDescription>Bénéficiaire: {voucherData.beneficiary_name}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Product Search */}
            <div>
              <Label>Ajouter un produit</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  className="pl-10"
                  placeholder="Rechercher un produit..."
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                />
              </div>
              {productResults.length > 0 && (
                <div className="mt-1 border rounded-lg max-h-48 overflow-y-auto">
                  {productResults.map((p, i) => (
                    <div
                      key={p.id || i}
                      className="flex items-center justify-between p-2 hover:bg-gray-50 cursor-pointer"
                      onClick={() => addProduct(p)}
                    >
                      <span className="text-sm">{p.name}</span>
                      <span className="text-sm text-gray-500">{p.price?.toLocaleString()} FCFA</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Products List */}
            <div>
              <Label>Produits ({voucherData.products.length})</Label>
              <div className="border rounded-lg divide-y">
                {voucherData.products.length === 0 ? (
                  <p className="p-4 text-sm text-gray-500 text-center">Aucun produit ajouté</p>
                ) : voucherData.products.map((p, i) => (
                  <div key={i} className={`flex items-center justify-between p-2 ${p.is_excluded ? 'bg-red-50' : ''}`}>
                    <div className="flex-1">
                      <span className="text-sm">{p.name}</span>
                      {p.is_excluded && (
                        <Badge variant="destructive" className="ml-2 text-xs">Exclu</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        className="w-16 h-8 text-center"
                        value={p.quantity}
                        onChange={e => updateProductQty(i, parseInt(e.target.value))}
                        min={1}
                      />
                      <span className="text-sm w-24 text-right">
                        {(p.unit_price * p.quantity).toLocaleString()} FCFA
                      </span>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => removeProduct(i)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <Card className="bg-cyan-50 border-cyan-200">
              <CardContent className="p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Total brut</span>
                  <span>{totals.gross.toLocaleString()} FCFA</span>
                </div>
                {totals.excluded > 0 && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Produits exclus</span>
                    <span>-{totals.excluded.toLocaleString()} FCFA</span>
                  </div>
                )}
                <div className="flex justify-between text-sm text-cyan-700">
                  <span>Part assurance ({voucherData.reimbursement_rate}%)</span>
                  <span>{totals.insurance.toLocaleString()} FCFA</span>
                </div>
                <div className="flex justify-between font-bold pt-2 border-t border-cyan-200">
                  <span>Reste à charge patient</span>
                  <span>{totals.patient.toLocaleString()} FCFA</span>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>Retour</Button>
              <Button onClick={submitVoucher} disabled={voucherData.products.length === 0}>
                <Check className="w-4 h-4 mr-2" />
                Valider le bon
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Done */}
      {step === 4 && (
        <Card className="text-center">
          <CardContent className="py-12">
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Bon enregistré !</h3>
            <p className="text-gray-600 mb-6">
              Part assurance: {totals.insurance.toLocaleString()} FCFA<br />
              Reste patient: {totals.patient.toLocaleString()} FCFA
            </p>
            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={() => { /* TODO: Print ticket */ }}>
                <Receipt className="w-4 h-4 mr-2" />
                Imprimer ticket
              </Button>
              <Button onClick={resetForm}>
                <Plus className="w-4 h-4 mr-2" />
                Nouveau bon
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Invoices Component
const InvoicesManager = ({ token, companies }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    insurance_id: '',
    start_date: '',
    end_date: ''
  });
  const [preview, setPreview] = useState(null);
  const [generating, setGenerating] = useState(false);

  const fetchInvoices = useCallback(async () => {
    try {
      const [invoicesRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/insurance/invoices/`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/insurance/invoices/summary`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setInvoices(invoicesRes.data.invoices);
      setSummary(summaryRes.data.summary);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const previewInvoice = async () => {
    if (!createForm.insurance_id || !createForm.start_date || !createForm.end_date) {
      toast.error('Remplissez tous les champs');
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/insurance/invoices/preview`, createForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const createInvoice = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/insurance/invoices/`, createForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Facture ${response.data.invoice_number} créée`);
      setShowCreateDialog(false);
      setPreview(null);
      setCreateForm({ insurance_id: '', start_date: '', end_date: '' });
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setGenerating(false);
    }
  };

  const downloadPDF = async (invoiceId) => {
    try {
      const response = await axios.get(`${API_URL}/insurance/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const markAsSent = async (invoiceId) => {
    try {
      await axios.put(`${API_URL}/insurance/invoices/${invoiceId}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Facture marquée comme envoyée');
      fetchInvoices();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      sent: 'bg-blue-100 text-blue-700',
      overdue: 'bg-red-100 text-red-700',
      paid: 'bg-green-100 text-green-700'
    };
    const labels = {
      draft: 'Brouillon',
      sent: 'Envoyée',
      overdue: 'En retard',
      paid: 'Payée'
    };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">En attente</p>
                  <p className="text-lg font-bold">{summary.sent?.amount?.toLocaleString() || 0} FCFA</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <div>
                  <p className="text-sm text-red-500">En retard</p>
                  <p className="text-lg font-bold text-red-600">{summary.overdue?.amount?.toLocaleString() || 0} FCFA</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <div>
                  <p className="text-sm text-green-500">Payées</p>
                  <p className="text-lg font-bold text-green-600">{summary.paid?.amount?.toLocaleString() || 0} FCFA</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center justify-center">
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle facture
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Invoices List */}
      <Card>
        <CardHeader>
          <CardTitle>Factures</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : invoices.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Aucune facture</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">N° Facture</th>
                    <th className="text-left p-2">Assurance</th>
                    <th className="text-left p-2">Période</th>
                    <th className="text-right p-2">Montant</th>
                    <th className="text-center p-2">Statut</th>
                    <th className="text-right p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.id} className="border-b hover:bg-gray-50">
                      <td className="p-2 font-medium">{inv.invoice_number}</td>
                      <td className="p-2">{inv.insurance_name}</td>
                      <td className="p-2">{inv.period?.start} - {inv.period?.end}</td>
                      <td className="p-2 text-right">{inv.amount?.toLocaleString()} FCFA</td>
                      <td className="p-2 text-center">{getStatusBadge(inv.status)}</td>
                      <td className="p-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => downloadPDF(inv.id)}>
                            <Download className="w-4 h-4" />
                          </Button>
                          {inv.status === 'draft' && (
                            <Button variant="ghost" size="sm" onClick={() => markAsSent(inv.id)}>
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Invoice Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nouvelle facture</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Assurance</Label>
              <Select value={createForm.insurance_id} onValueChange={v => setCreateForm({...createForm, insurance_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionnez une assurance" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date début</Label>
                <Input 
                  type="date" 
                  value={createForm.start_date} 
                  onChange={e => setCreateForm({...createForm, start_date: e.target.value})} 
                />
              </div>
              <div>
                <Label>Date fin</Label>
                <Input 
                  type="date" 
                  value={createForm.end_date} 
                  onChange={e => setCreateForm({...createForm, end_date: e.target.value})} 
                />
              </div>
            </div>

            <Button variant="outline" onClick={previewInvoice} className="w-full">
              <Eye className="w-4 h-4 mr-2" />
              Prévisualiser
            </Button>

            {preview && (
              <Card className="bg-gray-50">
                <CardContent className="p-4 space-y-2">
                  <p><strong>Assurance:</strong> {preview.insurance.name}</p>
                  <p><strong>Période:</strong> {preview.period.start} au {preview.period.end}</p>
                  <p><strong>Bons à facturer:</strong> {preview.vouchers_count}</p>
                  <p className="text-xl font-bold text-cyan-700">
                    Total: {preview.total_amount?.toLocaleString()} FCFA
                  </p>
                  <p className="text-sm text-gray-500">Échéance: {preview.due_date}</p>
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); setPreview(null); }}>
              Annuler
            </Button>
            <Button onClick={createInvoice} disabled={!preview || generating}>
              {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
              Créer la facture
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main SADASSUR App
const SADASSURApp = () => {
  const [token, setToken] = useState(localStorage.getItem('dermascan_token'));
  const [activeTab, setActiveTab] = useState('vouchers');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showExclusions, setShowExclusions] = useState(false);

  useEffect(() => {
    const fetchCompanies = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API_URL}/insurance/companies/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCompanies(response.data);
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };
    fetchCompanies();
  }, [token]);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Card className="w-96">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
            <p>Veuillez vous connecter pour accéder à SADASSUR</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 text-white p-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-2xl font-bold">SADASSUR</h1>
          <p className="text-cyan-100">Gestion des bons d'assurance</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="vouchers" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Traiter un bon</span>
            </TabsTrigger>
            <TabsTrigger value="companies" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              <span className="hidden sm:inline">Assurances</span>
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-2">
              <Receipt className="w-4 h-4" />
              <span className="hidden sm:inline">Factures</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              <span className="hidden sm:inline">Historique</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="vouchers">
            <VoucherProcessor token={token} companies={companies} />
          </TabsContent>

          <TabsContent value="companies">
            {showExclusions && selectedCompany ? (
              <ExclusionsManager 
                token={token} 
                company={selectedCompany} 
                onBack={() => { setShowExclusions(false); setSelectedCompany(null); }}
              />
            ) : (
              <InsuranceCompanies 
                token={token} 
                onSelectCompany={(c) => { setSelectedCompany(c); setShowExclusions(true); }}
              />
            )}
          </TabsContent>

          <TabsContent value="invoices">
            <InvoicesManager token={token} companies={companies} />
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Historique des bons traités</p>
                <p className="text-sm">Fonctionnalité en cours de développement</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SADASSURApp;
